import React, { useState, useEffect } from 'react';
import { RocketIcon, Loader, CheckCircle, AlertCircle, Clock, Minus, Plus, X, XCircle } from 'lucide-react'; // Added X and XCircle icons
import { FailedDocuments } from '../services/api/virtualAssistant';
import { FailedDocumentsResponse } from '../types/pipelineData';

interface Pipeline {
    id: string;
    run_type: string;
    start_ts: string;
    status: string;
    end_ts: string;  
    docs_processed_count: number;
    docs_failed_count: number;
    message: string;
    failedDocuments:[];
  }

interface currentStatus {
    currentStatus: {
        status?: string;
        error?: string;
      } | null; // currentStatus can now be null
}

interface PipelineStatusProps {
    currentStatus: {
        status?: string;
        error?: string;
      } | null; // currentStatus can be null or have status and error
    pipelineData: Pipeline[];
    expandedRows: Record<number, boolean>; 
    toggleExpandRow: (index: number) => void;
    expandedGetData: boolean;
    assistant: any;
  }
  
  const PipelineStatus: React.FC<PipelineStatusProps> = ({
    currentStatus,
    pipelineData,
    expandedRows,
    toggleExpandRow,
    expandedGetData,
    assistant }) => {

      // Keyed by run id, not row index: pipelineData is re-fetched by polling and a new run
      // is prepended, which shifts every index and would show one run's failures under another.
      const [failedDocs, setFailedDocs] = useState<Record<string, FailedDocumentsResponse>>({});
      const [failedDocsError, setFailedDocsError] = useState<Record<string, string>>({});
      const [currentPage, setCurrentPage] = useState<Record<string, number>>({});
      const [pageSize, setPageSize] = useState<Record<string, number>>({});

      const defaultPageSize = 5;

      const handlePageChange = (runId: string, newPage: number) => {
        setCurrentPage((prev) => ({ ...prev, [runId]: newPage }));
      };

      // Handle page size change
      const handlePageSizeChange = (runId: string, size: number) => {
        setPageSize((prev) => ({ ...prev, [runId]: size }));
        setCurrentPage((prev) => ({ ...prev, [runId]: 1 })); // Reset to first page
      };

      const [loadingFailedDocs, setLoadingFailedDocs] = useState<Record<string, boolean>>({});

      // Function to fetch failed documents when a row is expanded
      const fetchFailedDocuments = async (runId: string) => {
        setLoadingFailedDocs((prev) => ({ ...prev, [runId]: true })); // Set loading state
        setFailedDocsError((prev) => ({ ...prev, [runId]: '' }));
        try {
          const response = await FailedDocuments( runId, assistant?.app_id);
          setFailedDocs((prevDocs) => ({
            ...prevDocs,
            [runId]: response,
          }));
        } catch (error) {
          console.error('Error fetching failed documents:', error);
          setFailedDocsError((prev) => ({
            ...prev,
            [runId]: 'Could not load the failed document details. Please try again.',
          }));
        } finally {
          setLoadingFailedDocs((prev) => ({ ...prev, [runId]: false })); // Reset loading state
        }
      };

      // Helper function to get status icon and color
      const getStatusDisplay = (status: string) => {
        switch (status?.toLowerCase()) {
          case 'in progress':
            return {
              icon: <Loader className="animate-spin text-yellow-500 w-6 h-6" />,
              text: 'In Progress',
              color: 'text-yellow-600'
            };
          case 'complete':
            return {
              icon: <CheckCircle className="text-green-600 w-6 h-6" />,
              text: 'Complete',
              color: 'text-green-600'
            };
          case 'failed':
            return {
              icon: <AlertCircle className="text-red-600 w-6 h-6" />,
              text: 'Failed',
              color: 'text-red-600'
            };
          case 'cancelling':
            return {
              icon: <X className="animate-pulse text-orange-500 w-6 h-6" />,
              text: 'Cancelling',
              color: 'text-orange-600'
            };
          case 'cancelled':
            return {
              icon: <XCircle className="text-gray-600 w-6 h-6" />,
              text: 'Cancelled',
              color: 'text-gray-600'
            };
          case 'cancel_failed':
            return {
              icon: <AlertCircle className="text-red-600 w-6 h-6" />,
              text: 'Cancel Failed',
              color: 'text-red-600'
            };
          default:
            return {
              icon: <Clock className="text-gray-500 w-6 h-6" />,
              text: status || 'No active run',
              color: 'text-gray-500'
            };
        }
      };

      // Helper function to get pipeline run status display
      const getPipelineRunStatusDisplay = (status: string) => {
        switch (status?.toLowerCase()) {
          case 'success':
          case 'started':
            return {
              element: <span className="text-green-600 font-medium capitalize">{status.toUpperCase()}</span>
            };
          case 'cancelled':
            return {
              element: <span className="text-gray-600 font-medium capitalize">CANCELLED</span>
            };
          case 'cancelling':
            return {
              element: <span className="text-orange-600 font-medium capitalize">CANCELLING</span>
            };
          case 'cancel_failed':
            return {
              element: <span className="text-red-600 font-medium capitalize">CANCEL FAILED</span>
            };
          case 'failed':
          default:
            return {
              element: <span className="text-red-600 font-medium capitalize">{status.toUpperCase()}</span>
            };
        }
      };

      useEffect(() => {
      }, [failedDocs]);

  return (
    <div>
      {/* Expanded Section */}
      {expandedGetData && (
        <div className="mt-2 text-sm text-gray-700">
          {/* Current Status */}
          <div className="p-4 bg-white rounded-lg shadow-md border border-gray-200">
            <h4 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
              {/* Icon for Pipeline Run */}
              <RocketIcon className="w-6 h-6 text-black mr-2" />
              Latest Pipeline Run
            </h4>

            <div className="pl-2">
              {/* Status Section */}
              {currentStatus && (
                <>
                  <div className="flex items-center space-x-2 mb-3">
                    {(() => {
                      const statusDisplay = getStatusDisplay(currentStatus?.status || '');
                      return (
                        <>
                          {statusDisplay.icon}
                          <p className={`text-sm font-medium ${statusDisplay.color}`}>
                            {statusDisplay.text}
                          </p>
                        </>
                      );
                    })()}
                  </div>

                  {/* Error Message Section */}
                  {currentStatus?.error && (
                    <div className="mt-2 text-red-600 text-sm font-medium">
                      <p>Error: {currentStatus.error}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* History Section */}
          <div className="mb-2 mt-2 p-4 bg-white rounded-lg shadow-md border border-gray-200 overflow-x-auto">
            <h4 className="font-semibold mb-1">Run History</h4>
            <table className="min-w-full text-left border-t border-gray-200">
              <thead>
                <tr className="text-xs uppercase text-black">
                  <th className="py-1 px-2 text-left w-[5%]"></th> {/* New column for +,- button */}
                  <th className="py-1 px-2 text-left w-[10%] font-bold">Run Type</th>
                  <th className="py-1 px-2 text-left w-[15%]">Run Date</th>
                  <th className="py-1 px-2 text-left w-[10%]">Status</th>
                  <th className="py-1 px-2 text-left w-[10%]">Duration (hh:mm:ss)</th>
                  <th className="py-1 px-2 text-left w-[10%]">Processed</th>
                  <th className="py-1 px-2 text-left w-[10%]">Successful</th>
                  <th className="py-1 px-2 text-left w-[10%]">Failed</th>
                  <th className="py-1 px-2 text-left w-[30%]">Error</th>
                </tr>
              </thead>
              <tbody>
                {pipelineData && pipelineData?.map((run, idx) => {
                    const start = new Date(run.start_ts);
                    const end = run.end_ts ? new Date(run.end_ts) : null; // Handle missing end_ts
                    let duration = "In Progress...";

                    // Calculate duration if we have valid end time (works for all statuses including cancelled)
                    if (end && !isNaN(start.getTime()) && !isNaN(end.getTime())) { // Ensure valid dates
                      const durationMs = end.getTime() - start.getTime();
                      const totalSeconds = Math.floor(durationMs / 1000);
                      const hours = Math.floor(totalSeconds / 3600);
                      const minutes = Math.floor((totalSeconds % 3600) / 60);
                      const seconds = totalSeconds % 60;
                      if (hours > 0) {
                        duration = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} `;
                      } else if (minutes > 0) {
                        duration = `00:${minutes}:${String(seconds).padStart(2, '0')} `;
                      } else {
                        duration = `00:00:${seconds} `;
                      }
                    } else if (run.status?.toLowerCase() === 'cancelled' || run.status?.toLowerCase() === 'cancelling') {
                      // Special case: Cancelled but no end_ts (incomplete cancellation)
                      duration = "Cancelled";
                    }

                    const failedDocsResult = failedDocs[run.id];
                    const failedDocItems = failedDocsResult?.items ?? [];
                    const runPage = currentPage[run.id] || 1;
                    const runPageSize = pageSize[run.id] || defaultPageSize;
                    const totalPages = Math.max(1, Math.ceil(failedDocItems.length / runPageSize));
                  return (
                    <React.Fragment key={idx}>
                      <tr className="text-sm border-t">
                        {/* New Column for Expand/Collapse Button */}
                        <td className="py-1 px-2 text-left w-[5%]">
                          {run.docs_failed_count > 0 && (
                            <button
                              onClick={() => {
                                toggleExpandRow(idx);
                                if (!failedDocs[run.id] && !expandedRows[idx]) {
                                  fetchFailedDocuments(run.id); // Fetch failed docs when expanding
                                }
                              }}
                              className="text-gray-500 hover:text-gray-700 focus:outline-none"
                            >
                              {loadingFailedDocs[run.id] ? (
                                <Loader className="animate-spin text-gray-500 w-4 h-4" />
                              ) : expandedRows[idx] ? (
                                <Minus size={14} />
                              ) : (
                                <Plus size={14} />
                              )}
                            </button>
                          )}
                        </td>
                        {/* Run Type */}
                        <td className="py-1 px-2 text-left w-[10%]">{run.run_type}</td>
                        {/* Run Date */}
                        <td className="py-1 px-2 text-left w-[15%]">
                          {start.toLocaleDateString("en-US", { timeZone: "America/Chicago" })}{" "}
                          {start.toLocaleTimeString("en-US", { timeZone: "America/Chicago" })}
                        </td>
                        {/* Status */}
                        <td className="py-1 px-2 text-left w-[10%]">
                          {getPipelineRunStatusDisplay(run.status).element}
                        </td>
                        {/* Duration */}
                        <td className="py-1 px-2 text-left w-[10%]">{duration}</td>
                        {/* Processed Count */}
                        <td className="py-1 px-2 text-left text-green-600 w-[10%]"> 
                          {Number.isInteger(run.docs_processed_count + run.docs_failed_count)
                            ? run.docs_processed_count + run.docs_failed_count
                            : '-'}
                        </td>
                        {/* Successful Count */}
                        <td className="py-1 px-2 text-left text-green-600 w-[10%]">{run.docs_processed_count || '-'}</td>
                        {/* Failed Count */}
                        <td className="py-1 px-2 text-left text-red-500 w-[10%]">{run.docs_failed_count || '-'}</td>
                        {/* Error Message - Updated for cancel scenarios */}
                        <td className="py-1 px-2 text-left w-[30%]">
                          {run.status?.toLowerCase() === 'cancelled' ? (
                            <span className="text-gray-600">Operation was cancelled by user</span>
                          ) : run.status?.toLowerCase() === 'cancel_failed' ? (
                            <span className="text-red-500">Failed to cancel operation</span>
                          ) : (
                            <span className={run.message ? "text-red-500" : ""}>{run.message || '-'}</span>
                          )}
                        </td>
                      </tr>

                      {/* Expandable Row for Failed Documents */}
                      {expandedRows[idx] && run.docs_failed_count > 0 && (
                        <tr className="text-sm border-t bg-gray-50">
                          <td colSpan={9} className="py-2 px-3 text-left">
                            <p className="text-gray-700 font-semibold mb-1">Failed Documents:</p>
                            {loadingFailedDocs[run.id] ? (
                              <div className="flex items-center space-x-2 text-gray-600">
                                <Loader className="animate-spin w-5 h-5" />
                                <span>Loading failed documents...</span>
                              </div>
                            ) : failedDocsError[run.id] ? (
                              <div className="flex items-start space-x-2 text-red-600">
                                <AlertCircle className="w-5 h-5 shrink-0" />
                                <span>{failedDocsError[run.id]}</span>
                              </div>
                            ) : failedDocItems.length === 0 ? (
                              // The run record reports failures but no per-document rows came back.
                              // kai_document_list is updated in place by each run, so a re-run
                              // re-stamps those rows and this run's detail is no longer retrievable.
                              <p className="text-gray-600">
                                {run.docs_failed_count} document(s) failed in this run, but the
                                per-document detail is no longer available — it is not retained once
                                the data source is processed again.
                              </p>
                            ) : (
                              <>
                                {failedDocsResult?.truncated && (
                                  <p className="text-gray-600 text-xs mb-2">
                                    Showing the first {failedDocItems.length} of {failedDocsResult.total_count} failed documents.
                                  </p>
                                )}

                                {/* Paginated List */}
                                <ul className="space-y-2">
                                  {failedDocItems
                                    .slice((runPage - 1) * runPageSize, runPage * runPageSize)
                                    .map((doc) => (
                                      <li
                                      key={doc.id}
                                      className="flex items-start space-x-3 bg-gray-100 p-3 rounded-lg shadow-sm hover:bg-gray-200 transition-colors"
                                    >
                                      {/* Bullet */}
                                      <div className="w-2.5 h-2.5 bg-black rounded-full"></div>

                                      {/* Document info */}
                                      <div className="flex-1">
                                        {/* Document Name */}
                                        <p className="text-gray-800 text-sm font-medium">
                                          {doc.source_object_name || doc.source_object_id || doc.id}
                                        </p>

                                        {/* Error Message - only shown if exists */}
                                        {doc?.error_msg && (
                                          <p className="text-red-600 text-xs mt-1">
                                            {doc?.error_msg}
                                          </p>
                                        )}
                                      </div>
                                    </li>
                                    ))}
                                </ul>

                                {/* Pagination */}
                                {failedDocItems.length > runPageSize && (
                                  <div className="flex justify-center items-center gap-2 mt-4 mb-2">
                                    {/* Previous Button */}
                                    <button
                                      onClick={() => handlePageChange(run.id, runPage - 1)}
                                      disabled={runPage === 1}
                                      className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
                                    >
                                      Previous
                                    </button>

                                    {/* Page Numbers */}
                                    {Array.from({ length: totalPages }, (_, i) => {
                                      if (
                                        i + 1 === 1 ||
                                        i + 1 === totalPages ||
                                        (i + 1 >= runPage - 2 && i + 1 <= runPage + 2)
                                      ) {
                                        return (
                                          <button
                                            key={i + 1}
                                            onClick={() => handlePageChange(run.id, i + 1)}
                                            className={`px-3 py-1 border rounded-md ${
                                              runPage === i + 1 ? 'bg-gray-900 text-white' : 'hover:bg-gray-50'
                                            }`}
                                          >
                                            {i + 1}
                                          </button>
                                        );
                                      } else if (i + 1 === runPage - 3 || i + 1 === runPage + 3) {
                                        return <span key={i}>...</span>;
                                      }
                                      return null;
                                    })}

                                    {/* Next Button */}
                                    <button
                                      onClick={() => handlePageChange(run.id, runPage + 1)}
                                      disabled={runPage === totalPages}
                                      className="px-3 py-1 border rounded-md disabled:opacity-50 hover:bg-gray-50"
                                    >
                                      Next
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PipelineStatus;